import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { FeedbackAction } from './InlineAlert';
import type { Action, WithClassName } from './types';

export type ToastOptions = {
  id?: string; tone: 'success' | 'info' | 'warning' | 'danger'; title: string;
  message?: string; action?: Action; duration?: number | null; urgent?: boolean;
};
type ToastEntry = ToastOptions & { id: string };
type ToastApi = { push: (toast: ToastOptions) => string; dismiss: (id: string) => void };
type ToastContextValue = ToastApi & { entries: readonly ToastEntry[] };
const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [entries, setEntries] = useState<ToastEntry[]>([]);
  const sequence = useRef(0);
  const push = useCallback((options: ToastOptions) => {
    const id = options.id ?? `ui-toast-${++sequence.current}`;
    setEntries((current) => current.some((entry) => entry.id === id) ? current : [...current, { ...options, id }]);
    return id;
  }, []);
  const dismiss = useCallback((id: string) => setEntries((current) => current.filter((entry) => entry.id !== id)), []);
  const value = useMemo(() => ({ push, dismiss, entries }), [push, dismiss, entries]);
  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

function useToastContext() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('Toast feedback requires ToastProvider.');
  return context;
}

export function useToast(): ToastApi {
  const { push, dismiss } = useToastContext();
  return useMemo(() => ({ push, dismiss }), [push, dismiss]);
}

/** Mount once, beside App inside ToastProvider. Individual messages own announcements. */
export function ToastRegion({ className }: WithClassName) {
  const { entries, dismiss } = useToastContext();
  return createPortal(<section className={`ui-toast-region ${className ?? ''}`} aria-label="Notifications">
    {entries.slice(0, 3).map((entry) => <ToastItem key={entry.id} entry={entry} dismiss={dismiss} />)}
  </section>, document.body);
}

function ToastItem({ entry, dismiss }: { entry: ToastEntry; dismiss: (id: string) => void }) {
  const node = useRef<HTMLDivElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(document.hidden);
  const duration = entry.action || entry.tone === 'warning' || entry.tone === 'danger' ? null : entry.duration === undefined ? 6000 : entry.duration;
  const remaining = useRef(duration);
  useEffect(() => {
    const visibility = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', visibility);
    return () => document.removeEventListener('visibilitychange', visibility);
  }, []);
  useEffect(() => {
    if (remaining.current === null || hovered || focused || hidden) return;
    const started = Date.now();
    const timer = window.setTimeout(() => {
      // Check the DOM as well as state: a focus event and timer can share a tick.
      if (document.hidden) { setHidden(true); return; }
      if (node.current?.contains(document.activeElement)) { setFocused(true); return; }
      dismiss(entry.id);
    }, Math.max(0, remaining.current));
    return () => {
      window.clearTimeout(timer);
      if (remaining.current !== null) remaining.current = Math.max(0, remaining.current - (Date.now() - started));
    };
  }, [hovered, focused, hidden, dismiss, entry.id]);
  return <div ref={node} className={`ui-feedback ui-toast ui-tone-${entry.tone}`}
    role={entry.urgent && entry.tone === 'danger' ? 'alert' : 'status'}
    onPointerEnter={() => setHovered(true)} onPointerLeave={() => setHovered(false)}
    onFocus={() => setFocused(true)} onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false);
    }}>
    <div className="ui-toast__header">
      <p className="ui-feedback__title">{entry.title}</p>
      <button type="button" className="ui-button ui-button--ghost" aria-label={`Dismiss ${entry.title}${entry.message ? `: ${entry.message}` : ''}`}
        onClick={() => dismiss(entry.id)}>Dismiss</button>
    </div>
    {entry.message && <p>{entry.message}</p>}
    {entry.action && <FeedbackAction action={entry.action} />}
  </div>;
}
